/**
 * Finding Card Component
 * Displays individual audit finding with details, status, and actions
 */

import {
  Paper,
  Group,
  Badge,
  Text,
  Stack,
  ActionIcon,
  Collapse,
  Button,
  Divider,
  Checkbox,
  Modal,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronUp,
  IconAlertTriangle,
  IconStar,
  IconEye,
  IconFlag,
  IconMessage,
  IconPaperclip,
  IconEdit,
  IconBan,
} from "@tabler/icons-react";
import {
  AuditFinding,
  FindingSeverity,
  FindingStatus,
  FindingType,
} from "../../types/audit";
import { useState } from "react";
import { format } from "date-fns";
import { FindingResponseModal } from "./FindingResponseModal";
import { EditFindingModal } from "./EditFindingModal";
import {
  useFindingResponses,
  useWithdrawAuditFinding,
} from "../../hooks/useAuditAnnotations";
import { ResponseCard } from "./ResponseCard";
import { AttachmentList } from "./AttachmentList";
import { AttachmentUpload } from "./AttachmentUpload";
import { useAttachments } from "../../hooks/useAttachments";
import { useAuth } from "../../hooks/useAuth";
import { notifications } from "@mantine/notifications";

interface FindingCardProps {
  finding: AuditFinding;
  onUpdate?: () => void;
  showResponseButton?: boolean; // Show "Respond" button for facility staff
  showAttachmentUpload?: boolean; // Show attachment upload for auditors (within 24 hours)
  // Bulk selection props
  selectable?: boolean;
  selected?: boolean;
  onSelectionChange?: (findingId: string, selected: boolean) => void;
}

export function FindingCard({
  finding,
  onUpdate,
  showResponseButton = false,
  showAttachmentUpload = false,
  selectable = false,
  selected = false,
  onSelectionChange,
}: FindingCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [responseModalOpen, setResponseModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);

  // Fetch responses for this finding
  const { data: responses = [], isLoading: responsesLoading } =
    useFindingResponses(finding.id);

  // Fetch attachments for this finding
  const { data: attachments = [] } = useAttachments(finding.id);

  // Get current user to check if they can delete attachments
  const { data: authContext } = useAuth();

  // US-112 Phase 1b: withdraw (retract) instead of hard delete.
  const withdrawFinding = useWithdrawAuditFinding();
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const isWithdrawn = finding.withdrawn_at != null;

  // Check if user is the auditor who created this finding
  const isAuditor = authContext?.userId === finding.auditor_id;

  // Check if finding is within 24 hours (can upload/delete attachments/edit finding)
  const isWithin24Hours = () => {
    const createdTime = new Date(finding.created_at).getTime();
    const twentyFourHours = 24 * 60 * 60 * 1000;
    return new Date().getTime() - createdTime < twentyFourHours;
  };

  const canEditFinding = isAuditor && isWithin24Hours() && !isWithdrawn;
  // US-112 Phase 1b: the owner can withdraw while the finding is live; the server
  // enforces the engagement lock (responses / finalized session).
  const canWithdrawFinding = isAuditor && !isWithdrawn;
  const canUploadAttachments =
    isAuditor && isWithin24Hours() && showAttachmentUpload && !isWithdrawn;
  const canDeleteAttachments = isAuditor && isWithin24Hours() && !isWithdrawn;

  const handleWithdraw = async () => {
    const reason = withdrawReason.trim();
    if (!reason) return;
    try {
      await withdrawFinding.mutateAsync({
        id: finding.id,
        withdrawal_reason: reason,
      });
      notifications.show({
        title: "Finding Withdrawn",
        message: "The finding was withdrawn and is no longer counted as open.",
        color: "green",
      });
      setWithdrawModalOpen(false);
      setWithdrawReason("");
      onUpdate?.();
    } catch {
      // error toast comes from the global mutation handler (US-081)
    }
  };

  const getSeverityColor = (severity: FindingSeverity): string => {
    const colors = {
      critical: "red",
      high: "orange",
      medium: "yellow",
      low: "blue",
      informational: "gray",
    };
    return colors[severity];
  };

  const getStatusColor = (status: FindingStatus): string => {
    const colors = {
      open: "red",
      acknowledged: "blue",
      in_progress: "yellow",
      resolved: "green",
      closed: "gray",
    };
    return colors[status];
  };

  const getTypeIcon = (type: FindingType) => {
    switch (type) {
      case "deficiency":
        return <IconAlertTriangle size={16} />;
      case "commendation":
        return <IconStar size={16} />;
      case "observation":
        return <IconEye size={16} />;
      case "recommendation":
        return <IconFlag size={16} />;
      default:
        return <IconFlag size={16} />;
    }
  };

  const getTypeColor = (type: FindingType): string => {
    const colors = {
      deficiency: "red",
      observation: "blue",
      commendation: "green",
      recommendation: "yellow",
    };
    return colors[type];
  };

  return (
    <Paper
      p="sm"
      withBorder
      style={{
        border: selected ? "2px solid var(--mantine-color-blue-5)" : undefined,
      }}
    >
      {/* Header - Always Visible */}
      <Group justify="space-between" align="flex-start" mb="xs">
        <Group gap="xs" style={{ flex: 1 }} wrap="nowrap">
          {/* Selection Checkbox */}
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={(event) =>
                onSelectionChange?.(finding.id, event.currentTarget.checked)
              }
              aria-label={`Select finding ${finding.title}`}
            />
          )}

          <Stack gap={4} style={{ flex: 1 }}>
            <Group gap="xs">
              <Badge
                color={getTypeColor(finding.finding_type)}
                variant="light"
                leftSection={getTypeIcon(finding.finding_type)}
                size="sm"
              >
                {finding.finding_type}
              </Badge>
              <Badge color={getSeverityColor(finding.severity)} size="sm">
                {finding.severity}
              </Badge>
              <Badge
                color={getStatusColor(finding.status)}
                variant="dot"
                size="sm"
              >
                {finding.status}
              </Badge>
              {isWithdrawn && (
                <Tooltip
                  label={finding.withdrawal_reason ?? "Withdrawn"}
                  multiline
                  w={260}
                >
                  <Badge color="gray" variant="filled" size="sm">
                    Withdrawn
                  </Badge>
                </Tooltip>
              )}
              {attachments.length > 0 && (
                <Badge
                  color="gray"
                  variant="light"
                  size="sm"
                  leftSection={<IconPaperclip size={12} />}
                >
                  {attachments.length}
                </Badge>
              )}
            </Group>
            <Text fw={600} size="sm">
              {finding.title}
            </Text>
            {finding.section && (
              <Text size="xs" c="dimmed">
                Section: {finding.section}
              </Text>
            )}
          </Stack>
        </Group>

        <ActionIcon
          variant="subtle"
          onClick={() => setExpanded(!expanded)}
          size="sm"
        >
          {expanded ? (
            <IconChevronUp size={16} />
          ) : (
            <IconChevronDown size={16} />
          )}
        </ActionIcon>
      </Group>

      {/* Rating */}
      {finding.rating && (
        <Group gap={4} mb="xs">
          {[...Array(5)].map((_, i) => (
            <IconStar
              key={i}
              size={14}
              fill={i < Number(finding.rating) ? "gold" : "transparent"}
              color={i < Number(finding.rating) ? "gold" : "gray"}
            />
          ))}
          <Text size="xs" c="dimmed">
            ({finding.rating}/5)
          </Text>
        </Group>
      )}

      {/* Expanded Details */}
      <Collapse expanded={expanded}>
        <Stack gap="sm" mt="md">
          {/* Description */}
          <div>
            <Text size="xs" fw={600} c="dimmed" mb={4}>
              Description
            </Text>
            <Text size="sm">{finding.description}</Text>
          </div>

          {/* Recommendations */}
          {finding.recommendations && (
            <div>
              <Text size="xs" fw={600} c="dimmed" mb={4}>
                Recommendations
              </Text>
              <Text size="sm">{finding.recommendations}</Text>
            </div>
          )}

          {/* Regulatory Reference */}
          {finding.regulatory_reference && (
            <div>
              <Text size="xs" fw={600} c="dimmed" mb={4}>
                Regulatory Reference
              </Text>
              <Text size="sm" c="blue">
                {finding.regulatory_reference}
              </Text>
            </div>
          )}

          {/* Follow-up */}
          {finding.requires_follow_up && (
            <div>
              <Badge color="orange" variant="light" size="sm">
                Requires Follow-up
              </Badge>
              {finding.follow_up_date && (
                <Text size="xs" c="dimmed" mt={4}>
                  Follow-up by:{" "}
                  {format(new Date(finding.follow_up_date), "MMM d, yyyy")}
                </Text>
              )}
            </div>
          )}

          {/* Timestamps */}
          <div>
            <Text size="xs" c="dimmed">
              Created:{" "}
              {format(new Date(finding.created_at), "MMM d, yyyy h:mm a")}
            </Text>
            {finding.resolved_at && (
              <Text size="xs" c="dimmed">
                Resolved:{" "}
                {format(new Date(finding.resolved_at), "MMM d, yyyy h:mm a")}
              </Text>
            )}
          </div>

          {/* Edit (within 24h) / Withdraw (while live) — auditors only */}
          {(canEditFinding || canWithdrawFinding) && (
            <>
              <Divider />
              <Group gap="xs">
                {canEditFinding && (
                  <Button
                    variant="light"
                    color="blue"
                    size="sm"
                    leftSection={<IconEdit size={16} />}
                    onClick={() => setEditModalOpen(true)}
                  >
                    Edit Finding
                  </Button>
                )}
                {canWithdrawFinding && (
                  <Button
                    variant="light"
                    color="red"
                    size="sm"
                    leftSection={<IconBan size={16} />}
                    onClick={() => setWithdrawModalOpen(true)}
                  >
                    Withdraw Finding
                  </Button>
                )}
              </Group>
            </>
          )}

          {/* Attachments Section */}
          {(attachments.length > 0 || canUploadAttachments) && (
            <>
              <Divider />
              <div>
                <Group gap="xs" mb="sm">
                  <IconPaperclip size={16} />
                  <Text size="xs" fw={600} c="dimmed">
                    Attachments{" "}
                    {attachments.length > 0 && `(${attachments.length})`}
                  </Text>
                </Group>

                {/* Attachment List */}
                <AttachmentList
                  findingId={finding.id}
                  canDelete={canDeleteAttachments}
                />

                {/* Attachment Upload (auditors only, within 24 hours) */}
                {canUploadAttachments && (
                  <Stack gap="sm" mt="md">
                    <Text size="xs" fw={600} c="dimmed">
                      Add Attachment
                    </Text>
                    <AttachmentUpload
                      findingId={finding.id}
                      onUploadComplete={onUpdate}
                    />
                  </Stack>
                )}
              </div>
            </>
          )}

          {/* Responses Section */}
          {!responsesLoading && responses.length > 0 && (
            <>
              <Divider />
              <div>
                <Text size="xs" fw={600} c="dimmed" mb="sm">
                  Responses ({responses.length})
                </Text>
                <Stack gap="sm">
                  {responses.map((response) => (
                    <ResponseCard key={response.id} response={response} />
                  ))}
                </Stack>
              </div>
            </>
          )}

          {/* Respond Button (for facility staff) */}
          {showResponseButton && (
            <>
              <Divider />
              <Button
                variant="light"
                size="sm"
                leftSection={<IconMessage size={16} />}
                onClick={() => setResponseModalOpen(true)}
                fullWidth
              >
                Respond to Finding
              </Button>
            </>
          )}
        </Stack>
      </Collapse>

      {/* Response Modal */}
      {showResponseButton && (
        <FindingResponseModal
          opened={responseModalOpen}
          onClose={() => setResponseModalOpen(false)}
          finding={finding}
          onSuccess={() => {
            setResponseModalOpen(false);
            onUpdate?.();
          }}
        />
      )}

      {/* Edit Finding Modal */}
      <EditFindingModal
        opened={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        finding={finding}
        onSuccess={() => {
          setEditModalOpen(false);
          onUpdate?.();
        }}
      />

      {/* US-112 Phase 1b: Withdraw Finding Modal */}
      <Modal
        opened={withdrawModalOpen}
        onClose={() => setWithdrawModalOpen(false)}
        title="Withdraw finding"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Withdrawing retracts this finding from active counts and reports.
            The record is preserved (marked withdrawn) for audit integrity — it
            is not deleted. A finding that already has responses, or is in a
            finalized session, cannot be withdrawn.
          </Text>
          <Textarea
            label="Reason for withdrawal"
            placeholder="e.g. Duplicate of finding #123 / logged against the wrong resident"
            withAsterisk
            autosize
            minRows={3}
            value={withdrawReason}
            onChange={(e) => setWithdrawReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setWithdrawModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleWithdraw}
              loading={withdrawFinding.isPending}
              disabled={!withdrawReason.trim()}
            >
              Withdraw finding
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Paper>
  );
}
